import { useCallback, useState, type KeyboardEvent } from 'react';
import { useLocale } from '@/context/LocaleContext';

const MAX_LENGTH = 2000;

interface ChatInputProps {
  disabled: boolean;
  onSend: (message: string) => void;
}

export function ChatInput({ disabled, onSend }: ChatInputProps) {
  const { t } = useLocale();
  const [value, setValue] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  }, [disabled, onSend, value]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="chat-input-container">
      <div className="chat-input-wrapper">
        <textarea
          id="chat-input"
          aria-label={t('chat_input_placeholder')}
          aria-describedby="char-count"
          placeholder={t('chat_input_placeholder')}
          rows={1}
          maxLength={MAX_LENGTH}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          id="send-btn"
          className="send-btn"
          title={t('chat_send_title')}
          disabled={disabled || !value.trim()}
          onClick={handleSend}
        >
          <svg aria-hidden="true" width="18" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
          <span className="send-text">{t('chat_send_button')}</span>
        </button>
      </div>
      <div className="chat-input-info">
        <span id="char-count">{t('chat_char_count', { current: value.length, max: MAX_LENGTH })}</span>
      </div>
    </div>
  );
}
